import { MERCHANT_TERMS } from '@field-support/shared';
import type { catalogAr } from './catalog.ar';

/** Catalog copy (English). Must cover exactly the same keys as `catalogAr`. */
export const catalogEn: typeof catalogAr = {
  catalog: {
    showDeactivated: 'Show it',
    emptyBrandsFiltered: `No ${MERCHANT_TERMS.en.manyLower} match these filters.`,
    emptyBrandsNone: `No ${MERCHANT_TERMS.en.manyLower} yet.`,
    emptyBranchesFiltered: 'No branches match these filters.',
    emptyBranchesNone: 'No branches yet.',
    brands: `${MERCHANT_TERMS.en.many}`,
    branches: 'Branches',
    brandsDescription: `${MERCHANT_TERMS.en.many} agents choose from when recording a visit.`,
    branchesDescription: `Branches per ${MERCHANT_TERMS.en.oneLower}, with their verified location and geofence.`,
    countBrands: `${MERCHANT_TERMS.en.manyLower}`,
    countBrands_one: `${MERCHANT_TERMS.en.oneLower}`,
    countBrands_two: `${MERCHANT_TERMS.en.manyLower}`,
    countBrands_few: `${MERCHANT_TERMS.en.manyLower}`,
    countBranches: 'branches',
    countBranches_one: 'branch',
    countBranches_two: 'branches',
    countBranches_few: 'branches',
    searchBrands: `${MERCHANT_TERMS.en.one} name or code`,
    searchBranches: 'Branch name or code',
    newBrand: `New ${MERCHANT_TERMS.en.oneLower}`,
    newBranch: 'New branch',
    branchCount: 'Branches',
    location: 'Location',
    geofence: 'Geofence (m)',
    area: 'Area',
    areaHint: 'District or zone, e.g. Nasr City. Used to filter branches when planning visits.',
    allLocationStatuses: 'All location states',
    includeInactive: 'Show inactive',
    deletedHard: 'Deleted',
    deletedSoft: 'Has visit history – deactivated and hidden instead of deleted',
    deleteTitle: 'Delete {{name}}?',
    deleteBrandConsequence:
      'With no visit history, the merchant and its branches are deleted permanently. With visit history, they are deactivated and hidden instead, so their visits are kept.',
    deleteBranchConsequence:
      'With no visit history, the branch is deleted permanently. With visit history, it is deactivated and hidden instead, so its visits are kept.',
    setLocation: 'Set location',
    clickMap: 'Click the map or type coordinates.',
    latitude: 'Latitude',
    longitude: 'Longitude',
    locationsDescription: 'Verified branch locations and suggestions captured during visits.',
    suggestionsPending: 'location suggestions pending',
    suggestionsPending_one: 'location suggestion pending',
    suggestionsPending_two: 'location suggestions pending',
    suggestionsPending_few: 'location suggestions pending',
    noSuggestions: 'No location suggestions pending',
    suggestions: 'Location suggestions from visits',
    pendingLocation: 'Branches without a location',
    showOnMap: 'Show on map',
    accuracy: 'Accuracy',
    approve: 'Approve',
    reject: 'Reject',
    suggestedBy: 'Suggested by',
    allBrands: `All ${MERCHANT_TERMS.en.manyLower}`,
    verifiedBranch: 'Verified branch',
    suggestion: 'Suggestion',
  },
  geo: {
    nav: 'Zones and areas',
    deleteZoneConsequence:
      'The zone is deleted permanently. Only an empty zone can be deleted: move or delete its areas and branches first.',
    deleteAreaConsequence: 'The area is deleted permanently. Only an area with no branches can be deleted.',
    title: 'Zones and areas',
    description:
      'Zone → area → branch. Every branch belongs to one area; branches whose area is not known sit in their zone\'s "Unassigned" area. Names are compared ignoring spaces, case and Arabic letter variants.',
    zone: 'Zone',
    area: 'Area',
    allZones: 'All zones',
    allAreas: 'All areas',
    zones: 'Zones',
    areas: 'Areas',
    newZone: 'New zone',
    newArea: 'New area',
    editZone: 'Edit zone',
    editArea: 'Edit area',
    nameAr: 'Arabic name',
    nameEn: 'English name (optional)',
    code: 'Code',
    codeHint: 'Letters, digits, dot, dash. Stored in capitals.',
    managerNote: 'Manager note (optional)',
    branchCount: 'Branches',
    areaCount: 'Areas',
    unassigned: 'Unassigned',
    unassignedHint: 'Catch-all area of the zone: it cannot be renamed, moved or deleted.',
    inactive: 'Inactive',
    deleteRule: 'Only an empty area or zone can be deleted. Move its branches first, or deactivate it.',
    moveToArea: 'Move to area',
    moveTitle: 'Move {{count}} branches to an area',
    moveTitle_one: 'Move {{count}} branch to an area',
    moveTitle_two: 'Move {{count}} branches to an area',
    moveTitle_few: 'Move {{count}} branches to an area',
    moved: '{{count}} branches moved',
    moved_one: '{{count}} branch moved',
    moved_two: '{{count}} branches moved',
    moved_few: '{{count}} branches moved',
    chooseArea: 'Area',
    countZones: 'zones',
    countZones_one: 'zone',
    countZones_two: 'zones',
    countZones_few: 'zones',
    zoneOf: 'Zone',
    selectZoneFirst: 'Choose a zone to see its areas.',
    areaBranches: 'Branches of the area',
  },
  alerts: {
    emptyFiltered: 'No entries match these filters.',
    title: 'Branches typed by agents',
    description: 'Branch names agents typed because the branch was not in the catalog.',
    statusCount: 'branches waiting for review',
    statusCount_one: 'branch waiting for review',
    statusCount_two: 'branches waiting for review',
    statusCount_few: 'branches waiting for review',
    statusNone: 'No branches waiting for review',
    typedName: 'Typed name',
    verify: 'Add to catalog',
    verifyName: 'Branch name to add',
    override: 'Link to existing branch',
    chooseBranch: 'Official branch',
    dismiss: 'Dismiss',
    zohoUpdated: 'Zoho ticket updated',
    zohoFailed: 'Saved. The Zoho ticket was not updated: {{error}}',
    zohoNotNeeded: 'Saved. The Zoho ticket will use the corrected branch when it syncs.',
    retryZoho: 'Retry Zoho update',
    kindFilter: 'Type',
    kindAll: 'Branches and options',
    kind: { BRANCH: 'Branches', FIELD_OPTION: 'Form options (e.g. projects)' },
    optionOf: 'Typed for “{{field}}”',
    verifyOption: 'Add to the form',
    verifyOptionName: 'Option to add',
    optionVerifyNotice: 'Adds this option to “{{field}}” in the form draft. Phones get it after the form is published.',
    optionAdded: 'Added to the form draft. Publish the form to send it to the phones.',
    optionAlreadyListed: 'This option is already in the form draft.',
    address: 'Address',
    agentNote: 'Agent note',
    location: 'Agent location',
    locationSource: { REPORTED: 'When reporting', CHECK_OUT: 'At check-out', CHECK_IN: 'At check-in' },
    visit: 'Visit',
    merchantNotListed: 'Not in the list: added on verify',
    newMerchantNotice: 'The agent typed a merchant that is not in the list. Verifying creates this merchant, then the branch under it.',
    verifyMerchantName: 'Merchant name to add',
    chooseMerchant: 'Merchant',
    zohoError: 'Zoho error',
    zohoDetail: 'Technical detail', // new — needs native review
  },
};
